import { getIdToken } from './auth';
import { MOCK, mockApi } from './mock';
import type { Entry, Workout } from './types';

/** 器具ごとの前回値。いつの記録かを表示したいので日付も持つ。 */
export type LastEntry = Entry & { date: string };

export type SavePayload = {
  entries: Entry[];
  bodyWeight?: number;
  bodyFat?: number;
  note?: string;
};

let baseUrl = '';

export function initApi(apiUrl: string): void {
  baseUrl = apiUrl.replace(/\/$/, '');
}

/** ID トークンが取れなくなった (= 実質ログアウト) ときに投げる */
export class UnauthorizedError extends Error {
  constructor() {
    super('セッションが切れました。もう一度ログインしてください。');
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getIdToken();
  if (!token) throw new UnauthorizedError();

  const res = await fetch(`${baseUrl}${path}`, {
    ...init,
    headers: {
      authorization: `Bearer ${token}`,
      ...(init?.body ? { 'content-type': 'application/json' } : {}),
      ...init?.headers,
    },
  });

  if (res.status === 401 || res.status === 403) throw new UnauthorizedError();
  if (!res.ok) {
    const message = await res
      .json()
      .then((b: { message?: string }) => b.message)
      .catch(() => undefined);
    throw new Error(message ?? `リクエストに失敗しました (${res.status})`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

const realApi = {
  listWorkouts: (from: string, to: string) =>
    request<{ from: string; to: string; workouts: Workout[] }>(
      `/workouts?from=${from}&to=${to}`,
    ).then((r) => r.workouts),

  getWorkout: (date: string) =>
    request<{ date: string; workout: Workout | null }>(`/workouts/${date}`).then((r) => r.workout),

  /**
   * 入力補助データ。
   * lastByExercise = 器具名 → その器具を最後に記録した内容 (器具選択時に埋める)
   * recentWorkouts = 直近の記録 (褒め言葉のしきい値の基準)
   */
  getPrefill: (date: string) =>
    request<{ lastByExercise: Record<string, LastEntry>; recentWorkouts: Workout[] }>(
      `/prefill?date=${date}`,
    ),

  saveWorkout: (date: string, payload: SavePayload) =>
    request<{ date: string; workout: Workout | null }>(`/workouts/${date}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }).then((r) => r.workout),

  deleteWorkout: (date: string) => request<void>(`/workouts/${date}`, { method: 'DELETE' }),

  exportMarkdown: (to: string, days: number) =>
    request<{ from: string; to: string; days: number; markdown: string }>(
      `/export?to=${to}&days=${days}`,
    ),
};

/** モックビルドではローカル完結の実装に差し替わる (呼び出し側は同じ) */
export const api: typeof realApi = MOCK ? mockApi : realApi;
